import { Input, Spin } from "antd";
import { useState, useEffect } from "react";
import type {
  AnnotatedDeepLink,
  DigestV2State,
} from "pages/api/deep-links/types";
import { SearchOutlined } from "@ant-design/icons";
import styles from "./HomeBanner.module.css";

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  inputRef?: React.RefObject<any>;
  selectedDigest: any;
  placeholder?: string;
  loading?: boolean;
  enableSuggestions?: boolean;
}

export function SearchInput({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  inputRef,
  selectedDigest,
  loading,
  placeholder = "Explore your data",
  enableSuggestions = true,
}: SearchInputProps) {
  const [placeholderText, setPlaceholderText] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const searchSuggestions = [
    "Check out what has been released...",
    "Explore team performance metrics...",
    "Find recent development updates...",
    "View project delivery insights...",
    "Discover engineering analytics...",
  ];

  useEffect(() => {
    if (!enableSuggestions) {
      setPlaceholderText(placeholder);
      return;
    }
    if (value || isFocused || selectedDigest) {
      setPlaceholderText("");
      return;
    }

    let currentIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const typeText = () => {
      const currentText = searchSuggestions[currentIndex];
      let charIndex = 0;
      let isDeleting = false;

      const type = () => {
        if (!isDeleting && charIndex <= currentText.length) {
          setPlaceholderText(currentText.slice(0, charIndex));
          charIndex++;
          timeoutId = setTimeout(type, 50);
        } else if (!isDeleting && charIndex > currentText.length) {
          isDeleting = true;
          timeoutId = setTimeout(type, 1500);
        } else if (isDeleting && charIndex > 0) {
          charIndex--;
          setPlaceholderText(currentText.slice(0, charIndex));
          timeoutId = setTimeout(type, 30);
        } else if (isDeleting && charIndex === 0) {
          currentIndex = (currentIndex + 1) % searchSuggestions.length;
          timeoutId = setTimeout(typeText, 50);
        }
      };

      type();
    };

    timeoutId = setTimeout(typeText, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, isFocused, selectedDigest, enableSuggestions, placeholder]);

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={() => {
        setIsFocused(true);
        onFocus?.();
      }}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      placeholder={value ? "Search topics..." : placeholderText}
      prefix={<SearchOutlined />}
      className={styles.searchInput}
      size="large"
      addonAfter={loading ? <Spin /> : null}
    />
  );
}
